import type { Entitlement, EntitlementUpdate } from '@portal/pkg-api/gen/security'

/**
 * The section a permission with no feature falls into. The generator types `feature` as optional
 * while the wire value is `null`, so both have to land here — a falsy check rather than a
 * comparison against `undefined`.
 */
export const UNGROUPED = 'UNGROUPED'

export interface PermissionItem {
  /** Identity of an entitlement, and the key the granted list is matched on. */
  authority: string
  label: string
  checked: boolean
}

export interface PermissionGroup {
  /** `Feature` code, or `UNGROUPED`. */
  feature: string
  label: string
  items: PermissionItem[]
}

/**
 * The permissions grid: every entitlement the role may hold, grouped into the sections the
 * designs show, with the ones it currently holds ticked.
 *
 * Built from the available list rather than the granted one, so a permission the role holds that
 * is no longer on offer simply stops being shown — which is also what saving then does to it,
 * since the save sends exactly what the grid has ticked.
 */
export function buildPermissionGroups(
  available: Entitlement[],
  granted: Entitlement[],
  labelFor: (authority: string) => string,
  featureLabelFor: (feature: string) => string,
): PermissionGroup[] {
  const grantedAuthorities = new Set(granted.map((entitlement) => entitlement.authority))
  const groups = new Map<string, PermissionGroup>()

  for (const entitlement of available) {
    const feature = entitlement.target.feature || UNGROUPED
    let group = groups.get(feature)
    if (!group) {
      group = { feature, label: featureLabelFor(feature), items: [] }
      groups.set(feature, group)
    }
    group.items.push({
      authority: entitlement.authority,
      label: labelFor(entitlement.authority),
      checked: grantedAuthorities.has(entitlement.authority),
    })
  }

  for (const group of groups.values()) {
    group.items.sort((a, b) => a.label.localeCompare(b.label))
  }

  return [...groups.values()].sort(byLabelWithUngroupedLast)
}

/** Whatever the server could not place belongs at the end rather than alphabetised among the rest. */
function byLabelWithUngroupedLast(a: PermissionGroup, b: PermissionGroup) {
  if (a.feature === UNGROUPED) return 1
  if (b.feature === UNGROUPED) return -1
  return a.label.localeCompare(b.label)
}

/** Sections keep any permission whose label matches, and a section with none is dropped. */
export function filterGroups(groups: PermissionGroup[], search: string): PermissionGroup[] {
  const term = search.toLowerCase().trim()
  if (!term) return groups
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.label.toLowerCase().includes(term)),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * The body of the save: every ticked permission, stripped to the three members the server binds.
 *
 * A full replacement rather than a delta, so an unticked permission is one omitted. The derived
 * members a read returns — `authority` among them — are not accepted back, which is why this
 * rebuilds from the available list rather than sending what it received.
 */
export function toEntitlementUpdates(
  available: Entitlement[],
  checkedAuthorities: ReadonlySet<string>,
): EntitlementUpdate[] {
  return available
    .filter((entitlement) => checkedAuthorities.has(entitlement.authority))
    .map(({ target, permission, uniquifier }) => ({ target, permission, uniquifier }))
}
